import { InfoCircleOutlined } from '@ant-design/icons';
import type { Dayjs } from 'dayjs';
import { type ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';

import { Divider, Flex, VFlex } from '@/components/base';
import Button from '@/components/Button/Button';
import TooltipV2 from '@/components/Tooltip/TooltipV2';
import Heading from '@/components/Typography/Heading';
import Typo, { Paragraph, Span } from '@/components/Typography/Typo';
import SFilter from '@/features/StructuredFilter/class/SFilter';
import structuredFilterUtils from '@/features/StructuredFilter/utils/structuredFilter.utils';
import useStructuredFilter from '@/hooks/domain/App/useStructuredFilter';
import { modalStyle } from '@/styles/style';
import {
  FEEDBACK_COLUMNS,
  FeedbackColumnNameMap,
} from '@/types/filterKeyConst';
import { TOPIC_2_TYPE } from '@/types/taxonomyConst';
import { DATE_FORMAT, getTimezoneDayjs } from '@/utils/time';

import { Topic2TypeNameMap } from '../../TopicTypeLabel/TopicTypeLabel';

type Props = {
  topic?: Topic2T<TOPIC_2_TYPE>;
  closeModal: () => void;
};

const StaticTopicSettings = (props: Props) => {
  const { topic } = props;
  const { parseStructuredFilter } = useStructuredFilter();
  const [state, setState] = useState<{
    title: string;
    desc?: string;
    dateRange?: [Dayjs, Dayjs];
    dataFilter?: StructuredFilterQueryFormatT[];
    keywordFilters?: StructuredFilterStringNumberValueSet[];
    prompt?: string;
    aiKeywordFilter?: string;
  }>({
    title: '-',
  });

  const initializeFilter = useCallback(async () => {
    if (!topic) return;
    switch (topic.category) {
      case TOPIC_2_TYPE.AI: {
        const sFilter = new SFilter(topic.ai_rule.filter);
        const filterMap = sFilter.getDataFilterMap();
        const dataFilter = parseStructuredFilter(filterMap);
        const keywordFilters = sFilter.getSinlgeDataFilter(
          FEEDBACK_COLUMNS.SNIPPET,
        );
        setState((prev) => ({
          ...prev,
          title: topic.name,
          desc: topic.desc,
          keywordFilters: keywordFilters,
          prompt: topic.ai_rule.prompt,
          dataFilter: dataFilter,
        }));
        break;
      }

      case TOPIC_2_TYPE.RULE: {
        const sFilter = new SFilter(topic.filter_rule);
        const filterMap = sFilter.getDataFilterMap();
        const dataFilter = parseStructuredFilter(filterMap);
        const keywordFilters = sFilter.getSinlgeDataFilter(
          FEEDBACK_COLUMNS.FEEDBACK_TEXT,
        );
        setState((prev) => ({
          ...prev,
          title: topic.name,
          desc: topic.desc,
          keywordFilters: keywordFilters,
          dataFilter: dataFilter,
        }));
        break;
      }

      case TOPIC_2_TYPE.MANUAL: {
        setState((prev) => ({
          ...prev,
          title: topic.name,
          desc: topic.desc,
        }));
        break;
      }
      case TOPIC_2_TYPE.SAVED_TOPIC: {
        const sFilter = new SFilter(topic.saved_rule?.filter);

        const dateRangeFilter = sFilter.getDateFilter();

        const dateRange: [Dayjs, Dayjs] | undefined = dateRangeFilter
          ? [
              getTimezoneDayjs(dateRangeFilter.start_date),
              getTimezoneDayjs(dateRangeFilter.end_date),
            ]
          : undefined;

        const keywordFilter = sFilter.getSearchKeywordFilter();

        const filterMap = sFilter.getDataFilterMap();

        const dataFilter = parseStructuredFilter(filterMap);

        setState({
          title: topic.name,
          desc: topic.desc,
          dateRange: dateRange,
          keywordFilters: keywordFilter ? [keywordFilter] : undefined,
          aiKeywordFilter: topic.saved_rule?.query,
          dataFilter: dataFilter,
        });
        break;
      }
    }
  }, [parseStructuredFilter, topic]);

  useEffect(() => {
    initializeFilter();
  }, [initializeFilter]);

  const keywordValue = useMemo(() => {
    if (!state.keywordFilters) return null;
    if (state.keywordFilters.length === 0) return null;
    return state.keywordFilters.map((keywordFilter, index) => {
      const result =
        structuredFilterUtils.getStaticKeywordFilterString(keywordFilter);
      if (!result) return null;
      const { operatorString, valueString } = result;
      return (
        <Typo key={index}>
          <Typo className="text-gray-600">{operatorString}</Typo>
          &nbsp;&nbsp;
          <Typo>{valueString}</Typo>
        </Typo>
      );
    });
  }, [state.keywordFilters]);

  const dataFilterValue = useMemo(() => {
    if (!state?.dataFilter) return null;
    if (state.dataFilter.length === 0) return null;
    return state.dataFilter.map((filter, index) => {
      const columnName = FeedbackColumnNameMap.get(
        filter.columnKey as FEEDBACK_COLUMNS,
      );
      return (
        <Typo key={index}>
          <Typo>{columnName}</Typo>
          &nbsp;
          <Typo className="text-gray-600">
            {structuredFilterUtils.getOperatorTextMap({
              operator: filter.operator,
              isMultiValueField: true,
              hasMultiValues: filter.filterValues.length > 1,
            })}
          </Typo>
          &nbsp;
          <Typo>{filter.filterValues.map((v) => v.filterName).join(', ')}</Typo>
          <br />
        </Typo>
      );
    });
  }, [state?.dataFilter]);

  const dateString = state.dateRange
    ? (state.dateRange[0]?.format(DATE_FORMAT.readable) || '-')
    : undefined;

  const typeName = useMemo(() => {
    if (!topic) return null;
    return Topic2TypeNameMap[topic.category];
  }, [topic]);

  return (
    <Style>
      <Flex padding="32px 32px 24px 32px">
        <Heading type="h2">View details</Heading>
      </Flex>
      <Divider />
      <VFlex padding="24px 32px" g={20}>
        <FilterItemDisplay
          label="Type"
          value={<FilterValue>{typeName}</FilterValue>}
        />
        <FilterItemDisplay
          label="Title"
          value={<FilterValue>{topic?.name || '-'}</FilterValue>}
        />
        {topic?.desc && (
          <FilterItemDisplay
            label="Description"
            value={<FilterValue>{topic.desc}</FilterValue>}
          />
        )}

        {state.prompt && (
          <FilterItemDisplay
            label="Prompt"
            value={<FilterValue>{state.prompt}</FilterValue>}
          />
        )}

        {keywordValue && (
          <FilterItemDisplay
            label="Keywords"
            value={<FilterValue>{keywordValue}</FilterValue>}
          />
        )}

        {dateString && (
          <FilterItemDisplay
            label="Tracking begun on"
            tooltip="Beginning date for this topic tracking."
            value={<FilterValue>{dateString}</FilterValue>}
          />
        )}

        {dataFilterValue && (
          <FilterItemDisplay
            label="Filters"
            value={<FilterValue>{dataFilterValue}</FilterValue>}
          />
        )}

      </VFlex>
      <Divider />
      <Flex padding="24px 32px" j="flex-end">
        <Button variant="outlined" sizeType="s" onClick={props.closeModal}>
          Done
        </Button>
      </Flex>
    </Style>
  );
};

const Style = styled(VFlex)`
  ${modalStyle};
  width: 600px;
`;

const FilterItemDisplay = (props: {
  label: string;
  value: ReactNode;
  tooltip?: ReactNode;
}) => {
  return (
    <Flex g={4}>
      <FilterLabel>
        {props.label}
        {props.tooltip && (
          <TooltipV2 tooltipNode={props.tooltip}>
            <InfoCircleOutlined style={{ paddingLeft: 4 }} />
          </TooltipV2>
        )}
      </FilterLabel>
      {props.value}
    </Flex>
  );
};

const FilterLabel = styled(Span).attrs({
  size: 2,
})`
  color: ${(props) => props.theme.colors.gray600};
  width: 200px;
`;

const FilterValue = styled(Paragraph).attrs({
  size: 2,
})`
  flex: 1;
  color: ${(props) => props.theme.colors.gray800};
`;

export default StaticTopicSettings;
